import { Injectable } from '@angular/core';
import { addDays, differenceInCalendarDays, differenceInDays, set, subDays } from 'date-fns';

import { CalendarDay } from '../../../components/calendar-dates-view';
import { Calendar, CalendarDataSource, CalendarEvent, CalendarView } from '../calendar.models';
import { DEFAULT_CALENDAR_NAME, DEFAULT_CALENDAR_NAME_TOKEN } from '../utils/calendar-event.utils';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export const DAYS_IN_WEEK = 7;
export const DAYS_IN_WEEK_WITHOUT_WEEKENDS = 5;
export const DAYS_IN_3DAY = 3;

@Injectable({ providedIn: 'root' })
export class CalendarService {
  createCalendars(
    dataSource: CalendarDataSource[],
    sourceCalendars: Calendar[],
    hasEventsWithoutCalendar: boolean,
  ): Calendar[] {
    const calendars: Record<string, Calendar> = sourceCalendars.reduce(
      (group, calendar) => {
        if (calendar.name) {
          group[calendar.name] = calendar;
        }
        return group;
      },
      {} as Record<string, Calendar>,
    );
    // generate empty calendar if there are no events or any other calendars
    const includeEmpty =
      hasEventsWithoutCalendar ||
      (dataSource.length === 0 && sourceCalendars.filter((c) => c.id).length === 0);

    if (includeEmpty) {
      calendars[DEFAULT_CALENDAR_NAME] = {
        id: DEFAULT_CALENDAR_NAME_TOKEN,
        name: DEFAULT_CALENDAR_NAME,
        timeZone: null,
        events: {},
      };
    }

    for (const entry of dataSource) {
      const calendarName = entry.calendarName || DEFAULT_CALENDAR_NAME;

      if (calendarName && !calendars[calendarName]) {
        calendars[calendarName] = {
          id: entry.calendarId,
          name: calendarName,
          timeZone: entry.calendarTimeZone,
          events: {},
        };
      }

      const fromYear = entry.start.getFullYear();
      const fromMonth = entry.start.getMonth();
      const fromDate = entry.start.getDate();

      const toYear = entry.end.getFullYear();
      const toMonth = entry.end.getMonth();
      const toDate = entry.end.getDate();

      const events = calendars[calendarName].events;

      this.prepareCalendarEntry(events, entry.start);

      if (entry.end.getDate() === entry.start.getDate()) {
        events[fromYear][fromMonth][fromDate].push({
          id: entry.id,
          title: entry.title,
          startDate: entry.start,
          endDate: entry.end,
          timeZone: entry.calendarTimeZone,
          styles: entry.styles,
        });
      } else {
        events[fromYear][fromMonth][fromDate].push({
          id: entry.id,
          title: entry.title,
          startDate: entry.start,
          endDate: new Date(fromYear, fromMonth, fromDate, 23, 59, 59, 999),
          timeZone: entry.calendarTimeZone,
          styles: entry.styles,
          originalEndDate: entry.end,
          partial: 'start',
        });

        const daysDiff = differenceInCalendarDays(entry.end, entry.start);
        if (daysDiff > 1) {
          for (let i = 1; i < daysDiff; i++) {
            const date = addDays(entry.start, i);
            this.prepareCalendarEntry(events, date);
            events[date.getFullYear()][date.getMonth()][date.getDate()].push({
              id: entry.id,
              title: entry.title,
              startDate: set(date, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }),
              endDate: set(date, { hours: 23, minutes: 59, seconds: 59, milliseconds: 999 }),
              timeZone: entry.calendarTimeZone,
              styles: entry.styles,
              originalStartDate: entry.start,
              partial: 'mid',
            });
          }
        }

        this.prepareCalendarEntry(events, entry.end);
        events[toYear][toMonth][toDate].push({
          id: entry.id,
          title: entry.title,
          startDate: new Date(toYear, toMonth, toDate),
          endDate: entry.end,
          timeZone: entry.calendarTimeZone,
          styles: entry.styles,
          originalStartDate: entry.start,
          partial: 'end',
        });
      }
    }

    return Object.values(calendars).sort(this.sort);
  }

  parseTimeStringToObject(time: string): { hour: number; minute: number } {
    const [hour, minute] = time.split(':');

    return {
      hour: isNaN(+hour) ? 0 : Math.max(+hour, 0),
      minute: isNaN(+minute) ? 0 : Math.max(+minute, 0),
    };
  }

  dateDiffInDays(start: Date, end: Date): number {
    const startUTC = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
    const endUTC = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());

    return Math.floor((endUTC - startUTC) / MS_PER_DAY);
  }

  getStartOfTheWeek(date: Date, firstDayOfWeek: CalendarDay): Date {
    const dayOfWeek = date.getDay();
    const offset = (DAYS_IN_WEEK + dayOfWeek - firstDayOfWeek) % DAYS_IN_WEEK;

    const result = new Date(date);
    result.setDate(result.getDate() - offset);

    return result;
  }

  sort(a: { name: string | null }, b: { name: string | null }): number {
    return (a.name ?? '').localeCompare(b.name ?? '', undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }

  createWeekDateRange(
    date: Date,
    startOfWeek: CalendarDay,
    hideWeekends: boolean,
    view: CalendarView,
  ): Date[] {
    const weekLength = hideWeekends ? DAYS_IN_WEEK_WITHOUT_WEEKENDS : DAYS_IN_WEEK;
    const daysCount = view === CalendarView.ThreeDay ? DAYS_IN_3DAY : weekLength;
    const weekStartDate = this.getStartOfTheWeek(date, startOfWeek);
    const weekStartFromToday = differenceInDays(date, weekStartDate);
    const startDate =
      view !== CalendarView.ThreeDay || weekStartFromToday < 0 || weekStartFromToday >= weekLength
        ? weekStartDate
        : subDays(date, 1);
    const range = new Array(daysCount).fill(null).map((_, index) => {
      const date = new Date(startDate);
      date.setDate(date.getDate() + index);
      return date;
    });
    return range;
  }

  private prepareCalendarEntry(
    events: {
      [year: number]: {
        [month: number]: {
          [date: number]: CalendarEvent[];
        };
      };
    },
    calendarDate: Date,
  ): void {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const date = calendarDate.getDate();

    if (!events[year]) {
      events[year] = {};
    }

    if (!events[year][month]) {
      events[year][month] = {};
    }

    if (!events[year][month][date]) {
      events[year][month][date] = [];
    }
  }
}
